import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plane, User, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useFormValidation } from '../hooks/useFormValidation';
import { VALIDATION_SCHEMAS } from '../utils/validators';
import { sanitizeUserInput } from '../utils/sanitizer';

const Login = () => {
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { signIn } = useAuth();

  const {
    formData,
    errors,
    updateField,
    validateForm,
    getSanitizedData,
    hasFieldError,
    getFieldError
  } = useFormValidation(
    { email: '', password: '' },
    VALIDATION_SCHEMAS.LOGIN // ✅ Usa esquema LOGIN (NO valida fortaleza)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar formulario
    if (!validateForm()) {
      return;
    }

    // Sanitizar datos de entrada
    const sanitizedData = getSanitizedData();

    try {
      const { error } = await signIn(sanitizedData);
      if (error) {
        setError(error.message || 'Credenciales incorrectas');
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
    }
  };

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
        className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6, type: "spring", stiffness: 100 }}
      >
        <div className="flex flex-col items-center mb-8">
          <motion.div 
            className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
          >
            <Plane className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
            App Agencias
          </h1>
          <p className="text-gray-500 text-lg">Inicia sesión para gestionar tus viajes</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="sr-only">Email</label>
            <div className="relative">
              <User className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="Email"
                required
                className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg ${
                  hasFieldError('email') ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {hasFieldError('email') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('email')}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="password" className="sr-only">Contraseña</label>
            <div className="relative">
              <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => updateField('password', e.target.value)}
                placeholder="Contraseña"
                required
                className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg ${
                  hasFieldError('password') ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <motion.button
                type="button"
                onClick={() => setShowPassword(!showPassword)} // Toggle showPassword state
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </motion.button>
            </div>
            {hasFieldError('password') && (
              <p className="text-red-500 text-sm mt-1">{getFieldError('password')}</p>
            )}
          </div>

          {error && (
            <motion.p 
              className="text-red-600 text-center font-medium"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.p>
          )}

          <motion.button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98, y: 0 }}
          >
            Iniciar Sesión
          </motion.button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-8">
          ¿Problemas para iniciar sesión? Contacta a soporte.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default Login;